import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import LinkedText from '../LinkedText';

function tenure(roles) {
  const current = roles[0];
  const earliest = roles[roles.length - 1];
  if (roles.length === 1) return `${current.start} - ${current.end}`;
  return `${earliest.start} - ${current.end}`;
}

function roleDates(role) {
  if (role.start && role.end) return `${role.start} - ${role.end}`;
  return role.start || role.end || "";
}

function roleMeta(role) {
  return [roleDates(role), role.location].filter(Boolean).join(" · ");
}

const ExperienceModal = ({ experienceData }) => {
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (isLightboxOpen && modalRef.current) {
      modalRef.current.style.overflow = 'hidden';
    } else if (modalRef.current) {
      modalRef.current.style.overflow = 'auto';
    }
  }, [isLightboxOpen]);

  const handleClose = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const openLightbox = (index) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedImageIndex(null);
  };

  const navigateImage = useCallback((direction) => {
    if (!experienceData.gallery) return;

    const mediaItems = experienceData.gallery;
    const totalItems = mediaItems.length;
    if (direction === 'next') {
      setSelectedImageIndex((prev) => (prev + 1) % totalItems);
    } else {
      setSelectedImageIndex((prev) => (prev - 1 + totalItems) % totalItems);
    }
  }, [experienceData.gallery]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isLightboxOpen) {
          closeLightbox();
        } else {
          handleClose();
        }
      }
      if (isLightboxOpen) {
        if (e.key === 'ArrowLeft') {
          navigateImage('prev');
        } else if (e.key === 'ArrowRight') {
          navigateImage('next');
        }
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isLightboxOpen, selectedImageIndex, handleClose, navigateImage]);

  if (!experienceData) return null;

  const roles = experienceData.roles;
  const currentRole = roles[0];

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 backdrop-blur-sm animate-fade-in"
        onClick={handleBackdropClick}
      >
        <div
          ref={modalRef}
          className="relative w-full max-w-5xl bg-black my-8 rounded-lg shadow-2xl animate-slide-up overflow-y-auto"
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-50 w-10 h-10 flex items-center justify-center bg-black/80 hover:bg-black rounded-full transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="relative w-full h-[60vh] overflow-hidden rounded-t-lg">
            <img
              src={experienceData.heroImage}
              alt={`${experienceData.company} hero`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

            <div className="absolute bottom-0 left-0 p-8 md:p-12 w-full">
              <div className="mb-4">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {experienceData.company}
                </h1>
                <p className="text-xl md:text-2xl text-gray-300">
                  {currentRole.title}
                </p>
              </div>

              <div className="flex flex-wrap gap-4 text-sm md:text-base text-gray-300">
                <span className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {tenure(roles)}
                </span>
                <span className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {currentRole.location || experienceData.location}
                </span>
              </div>
            </div>
          </div>

          <div className="p-8 md:p-12 space-y-8">
            {roles.length > 1 ? (
              <section>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Roles</h2>
                <div className="space-y-8">
                  {roles.map((role, index) => {
                    const meta = roleMeta(role);
                    return (
                      <div
                        key={`${role.title}-${index}`}
                        className="border-t border-white/10 pt-8 first:border-t-0 first:pt-0"
                      >
                        <h3 className="text-xl md:text-2xl text-white">{role.title}</h3>
                        {meta && (
                          <p className="mt-1 text-sm md:text-base text-gray-400">{meta}</p>
                        )}
                        {role.summary && (
                          <p className="mt-3 text-lg text-gray-300 leading-relaxed">
                            <LinkedText text={role.summary} />
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : (
              <section>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">About</h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  <LinkedText text={currentRole.summary} />
                </p>
              </section>
            )}

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Tech Stack</h2>
              <div className="flex flex-wrap gap-3">
                {experienceData.techStack.map((tech, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-gray-800 text-white rounded-full text-sm font-medium hover:bg-gray-700 transition-colors"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </section>

            {experienceData.gallery && experienceData.gallery.length > 0 && (
              <section>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {experienceData.gallery.map((item, index) => (
                    <div
                      key={index}
                      className="relative aspect-video rounded-lg overflow-hidden group cursor-pointer"
                      onClick={() => (item.type === 'image' || item.type === 'video') && openLightbox(index)}
                    >
                      {item.type === 'image' ? (
                        <>
                          <img
                            src={item.src}
                            alt={item.alt}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                            <svg
                              className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </>
                      ) : item.type === 'video' ? (
                        <>
                          <video
                            src={item.src}
                            className="w-full h-full object-cover"
                            muted
                            loop
                            playsInline
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                            <svg
                              className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                            </svg>
                          </div>
                        </>
                      ) : null}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {experienceData.documents && experienceData.documents.length > 0 && (
              <section>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">{experienceData.documentTitle || 'Documentation'}</h2>
                <div className="grid grid-cols-1 gap-4">
                  {experienceData.documents.map((item, index) => (
                    <div
                      key={index}
                      className="group relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-8 border-2 border-gray-700 hover:border-white transition-all duration-300 hover:shadow-2xl hover:shadow-white/10"
                    >
                      <div className="flex items-center gap-6">
                        <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-red-600 to-red-800 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <svg
                            className="w-10 h-10 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gray-200 transition-colors">
                            {item.title || 'View PDF'}
                          </h3>
                          <p className="text-gray-400 mb-3">{item.alt}</p>
                          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-center">
                            {item.type === 'pdf' && (
                              <button
                                onClick={() => {
                                  setPdfLoading(true);
                                  setPreviewPdf(item);
                                }}
                                className="flex items-center gap-2 text-white font-semibold hover:text-red-400 transition-colors group/btn"
                              >
                                <span>Preview</span>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                            )}

                            {item.externalLink && (
                              <a
                                href={item.externalLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-gray-400 font-semibold hover:text-white transition-colors"
                              >
                                <span>{item.externalLabel ?? 'bioRxiv'}</span>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            )}

                            <a
                              href={item.src}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center justify-center sm:justify-start gap-2 text-white font-semibold hover:text-red-400 transition-colors ${item.type !== 'pdf' ? 'bg-red-600/20 px-4 py-2 rounded-md border border-red-600/30' : 'text-sm opacity-60 hover:opacity-100'}`}
                            >
                              <span>{item.type === 'link' ? 'Open Publication' : 'Download'}</span>
                              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                              </svg>
                            </a>
                          </div>


                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {experienceData.links && (
              <section className="flex flex-wrap gap-4 pt-4 border-t border-gray-800">
                {experienceData.links.company && (
                  <a
                    href={experienceData.links.company}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white rounded-md font-semibold border border-white/20 hover:bg-white/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    Visit Company Website
                  </a>
                )}
                {experienceData.links.apps?.map((app) => (
                  <a
                    key={app.href}
                    href={app.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white rounded-md font-semibold border border-white/20 hover:bg-white/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                    {app.label}
                  </a>
                ))}
                {experienceData.links.linkedin && (
                  <a
                    href={experienceData.links.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white rounded-md font-semibold border border-white/20 hover:bg-white/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    LinkedIn
                  </a>
                )}
              </section>
            )}
          </div>
        </div>
      </div>

      {isLightboxOpen && selectedImageIndex !== null && experienceData.gallery && experienceData.gallery.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-50 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close lightbox"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('prev');
            }}
            className="absolute left-4 z-50 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('next');
            }}
            className="absolute right-4 z-50 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Next image"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div
            className="relative max-w-7xl max-h-[90vh] w-full h-full flex flex-col items-center justify-center p-8 gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            {experienceData.gallery[selectedImageIndex].type === 'image' ? (
              <img
                src={experienceData.gallery[selectedImageIndex].src}
                alt={experienceData.gallery[selectedImageIndex].alt}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            ) : (
              <video
                src={experienceData.gallery[selectedImageIndex].src}
                controls
                autoPlay
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            )}
            {experienceData.gallery[selectedImageIndex].alt && (
              <div className="text-white text-center text-sm md:text-base bg-black/70 px-6 py-3 rounded-lg max-w-2xl">
                {experienceData.gallery[selectedImageIndex].alt}
              </div>
            )}
          </div>

          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
            {selectedImageIndex + 1} / {experienceData.gallery.length}
          </div>
        </div>
      )}

      {previewPdf && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget && window.innerWidth >= 640) {
              setPreviewPdf(null);
              setPdfLoading(false);
            }
          }}
        >
          <button
            onClick={() => {
              setPreviewPdf(null);
              setPdfLoading(false);
            }}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 z-50 w-14 h-14 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close preview"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Open in New Tab button - Mobile only */}
          <a
            href={previewPdf.src}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-2 left-2 sm:hidden z-50 w-14 h-14 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Open in new tab"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>

          <div
            className="relative w-full h-full max-w-6xl max-h-[90vh] p-0 sm:p-4 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {pdfLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white rounded-none sm:rounded-lg z-10">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
                <div className="text-gray-600 font-medium">Loading PDF...</div>
              </div>
            )}
            <iframe
              src={previewPdf.src}
              title={previewPdf.title || 'PDF Preview'}
              className="w-full h-full rounded-none sm:rounded-lg bg-white"
              onLoad={() => setPdfLoading(false)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ExperienceModal;
